/**
 * Team day view (owner ask 2026-10-16, the recruiters' request): one column
 * per recruiter for ONE day, side by side, so who is free when can be read by
 * eye. Pure helpers only (no React, no fetch); the grid is TeamDayView.tsx.
 *
 * Times are the viewer's local wall clock, like the rest of My Calendar.
 */
import { CalendarItem, TeamMember } from '../../types';

export interface TeamColumn {
  /** The recruiter's user id; null is the "Unassigned" column (open callbacks). */
  id: string | null;
  name: string;
  isMe: boolean;
}

export interface Selection {
  all: boolean;
  ids: string[];
}

/** One timed item placed on a column: minutes from local midnight, and its lane. */
export interface PlacedItem<T extends { title: string } = CalendarItem> {
  item: T;
  startMin: number;
  endMin: number;
  lane: number;
  lanes: number;
}

export const DEFAULT_START_HOUR = 7;
export const DEFAULT_END_HOUR = 19;
/** The shortest block drawn, so a zero-length item can still be clicked. */
export const MIN_BLOCK_MIN = 15;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const nextDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);

/**
 * The columns, in the order the recruiter picker lists people: you first, then
 * everyone else by name. "All recruiters" is the whole team. An id the team
 * list no longer has (someone who left) still gets a column. An "Unassigned"
 * column closes the row when the day has an unassigned item.
 */
export function teamColumns(
  team: TeamMember[],
  selection: Selection,
  meId: string | undefined,
  dayItems: CalendarItem[]
): TeamColumn[] {
  const byId = new Map(team.map((m) => [m.id, m]));
  const ids = selection.all ? team.map((m) => m.id) : [...new Set(selection.ids)];
  const cols: TeamColumn[] = ids.map((id) => ({
    id,
    name: byId.get(id)?.name || 'Teammate',
    isMe: id === meId,
  }));
  cols.sort((a, b) => (a.isMe === b.isMe ? a.name.localeCompare(b.name) : a.isMe ? -1 : 1));
  if (dayItems.some((it) => !it.owner_user_id)) cols.push({ id: null, name: 'Unassigned', isMe: false });
  return cols;
}

/** The items of one column (owner match; null owner = the Unassigned column). */
export function itemsForColumn(items: CalendarItem[], columnId: string | null): CalendarItem[] {
  return items.filter((it) => (it.owner_user_id || null) === columnId);
}

/**
 * Split a column's items for `day`: all-day items (and anything covering the
 * whole day) go to the top row; timed items are clipped to the day and given
 * minutes from local midnight. Items that miss the day are dropped.
 */
export function splitDay(
  items: CalendarItem[],
  day: Date
): { allDay: CalendarItem[]; timed: { item: CalendarItem; startMin: number; endMin: number }[] } {
  const from = startOfDay(day).getTime();
  const to = nextDay(day).getTime();
  const allDay: CalendarItem[] = [];
  const timed: { item: CalendarItem; startMin: number; endMin: number }[] = [];
  for (const it of items) {
    const s = new Date(it.start).getTime();
    const eRaw = new Date(it.end).getTime();
    if (!Number.isFinite(s)) continue;
    const e = Number.isFinite(eRaw) && eRaw > s ? eRaw : s;
    if (it.all_day) {
      // An all-day row's end is the next midnight (exclusive) or the same day.
      if (s < to && (e > from || startOfDay(new Date(s)).getTime() === from)) allDay.push(it);
      continue;
    }
    if (s >= to || (s < from && e <= from)) continue;
    if (s <= from && e >= to) {
      allDay.push(it);
      continue;
    }
    const cs = Math.max(s, from);
    const ce = Math.min(e, to);
    const startMin = Math.round((cs - from) / 60000);
    const endMin = Math.max(Math.round((ce - from) / 60000), startMin);
    timed.push({ item: it, startMin, endMin });
  }
  return { allDay, timed };
}

/**
 * The hours the grid shows: 7 AM to 7 PM, stretched to the earliest start and
 * latest end of any timed item on screen (whole hours, 0 to 24).
 */
export function gridHours(timed: { startMin: number; endMin: number }[]): { startHour: number; endHour: number } {
  let startHour = DEFAULT_START_HOUR;
  let endHour = DEFAULT_END_HOUR;
  for (const t of timed) {
    startHour = Math.min(startHour, Math.floor(t.startMin / 60));
    endHour = Math.max(endHour, Math.ceil(Math.max(t.endMin, t.startMin + MIN_BLOCK_MIN) / 60));
  }
  return { startHour: Math.max(0, startHour), endHour: Math.min(24, Math.max(endHour, startHour + 1)) };
}

/**
 * Side-by-side lanes for overlapping items in one column. Items that overlap
 * (directly or through a chain) form a cluster; each takes the first free lane,
 * and every item in a cluster shares its lane count. Edges that only touch do
 * not overlap. A block shorter than MIN_BLOCK_MIN is laid out at that length,
 * as it is drawn. Any titled item lays out (the callback slot picker's Team
 * mode passes its busy blocks).
 */
export function layoutLanes<T extends { title: string }>(
  timed: { item: T; startMin: number; endMin: number }[]
): PlacedItem<T>[] {
  const sorted = [...timed].sort(
    (a, b) => a.startMin - b.startMin || b.endMin - a.endMin || a.item.title.localeCompare(b.item.title)
  );
  const out: PlacedItem<T>[] = [];
  let cluster: PlacedItem<T>[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = laneEnds.length || 1;
    for (const p of cluster) p.lanes = lanes;
    out.push(...cluster);
    cluster = [];
    laneEnds = [];
    clusterEnd = -1;
  };
  for (const t of sorted) {
    const drawnEnd = Math.max(t.endMin, t.startMin + MIN_BLOCK_MIN);
    if (cluster.length && t.startMin >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= t.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(drawnEnd);
    } else {
      laneEnds[lane] = drawnEnd;
    }
    cluster.push({ ...t, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, drawnEnd);
  }
  if (cluster.length) flush();
  return out;
}

/** The local time a click lands on: `offsetMin` minutes below the grid's first hour, snapped down to 15. */
export function slotAt(day: Date, startHour: number, offsetMin: number): Date {
  const total = startHour * 60 + Math.max(0, offsetMin);
  const snapped = Math.floor(total / 15) * 15;
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(snapped / 60), snapped % 60, 0, 0);
}

/** "9 AM", "12 PM" — the grid's hour labels. */
export function hourLabel(h: number): string {
  const hh = h % 24;
  const suffix = hh < 12 ? 'AM' : 'PM';
  const n = hh % 12 === 0 ? 12 : hh % 12;
  return `${n} ${suffix}`;
}
